import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeButton } from '../common/NativeButton';
import { importDatePrecisionCounts, type CvImportEvidenceEntry } from '../../lib/cv-import-evidence';
import { useCvImportEvidence, type CvImportEvidenceNoticeProps } from './use-cv-import-evidence';

function ImportEntry({ entry, previous }: { entry: CvImportEvidenceEntry; previous?: boolean }) {
  const { t } = useTranslation('seeker');
  const dates = importDatePrecisionCounts(entry.extracted ?? entry.parsed);
  const sections = entry.quality?.missingDeclaredSections.map((section) => t(`cv.importEvidence.sections.${section}`)).join(', ');
  const plainText = { whiteSpace: 'pre-wrap' as const, overflowWrap: 'anywhere' as const, font: 'inherit', maxHeight: 320, overflowY: 'auto' as const };
  return <section style={{ marginBlockStart: 16 }}>
    <h4>{t(previous ? 'cv.importEvidence.previous' : 'cv.importEvidence.current')}</h4>
    <p>{t('cv.importEvidence.captured', { date: new Date(entry.capturedAt).toLocaleString() })}</p>
    {sections ? <p>{t('cv.importEvidence.missing', { sections })}</p> : null}
    {entry.quality?.fallbackUsed ? <p>{t('cv.importEvidence.fallback')}</p> : null}
    {!entry.quality ? <p>{t('cv.importEvidence.qualityUnavailable')}</p> : null}
    <p>{t('cv.importEvidence.datePrecision')}</p>
    {dates.year + dates.month + dates.unknown > 0 ? <p>{t('cv.importEvidence.dateCounts', dates)}</p> : null}
    <p>{t('cv.importEvidence.originalUnavailable')}</p>
    {entry.sourceTextTruncated ? <p>{t('cv.importEvidence.truncated')}</p> : null}
    <h4>{t('cv.importEvidence.source')}</h4>
    <div dir="auto" style={plainText}>{entry.sourceText ?? t('cv.importEvidence.sourceUnavailable')}</div>
    {entry.extracted ? <><h4>{t('cv.importEvidence.extracted')}</h4><div dir="auto" style={plainText}>{JSON.stringify(entry.extracted, null, 2)}</div></> : null}
    <h4>{t('cv.importEvidence.parsed')}</h4>
    <div dir="auto" style={plainText}>{JSON.stringify(entry.parsed, null, 2)}</div>
  </section>;
}

export function CvImportEvidenceNotice(props: CvImportEvidenceNoticeProps) {
  const { t } = useTranslation('seeker');
  const [expanded, setExpanded] = useState(false);
  const evidence = useCvImportEvidence(props);
  if (!evidence.allowed || (!evidence.data && !evidence.isError)) return null;
  return <section style={{ padding: 16, marginBlockEnd: 16, borderRadius: 16, color: 'var(--text-secondary)', backgroundColor: 'var(--tabbio-surface)' }}>
    <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)', fontSize: 16 }}>{t('cv.importEvidence.title')}</h3>
    <p>{t(evidence.isError ? 'cv.importEvidence.loadFailed' : 'cv.importEvidence.privateNotice')}</p>
    <NativeButton title={t(evidence.isError ? 'common:actions.retry' : expanded ? 'cv.importEvidence.hide' : 'cv.importEvidence.details')} size="sm" variant="ghost" onPress={() => evidence.isError ? void evidence.refetch() : setExpanded((value) => !value)} />
    {expanded && evidence.data ? <><ImportEntry entry={evidence.data.current} />{evidence.data.previous ? <ImportEntry entry={evidence.data.previous} previous /> : null}</> : null}
  </section>;
}
